<template>
    <div class="row">
        <div class="col-sm-12">
            <div class="card">
                <div class="card-body">
                    <div class="table-responsive table-desi table-product">
                        <v-data-table-server
                            class="table table-1d all-package"
                            v-model:items-per-page="itemsPerPage"
                            :search="search"
                            :headers="headers"
                            :items-length="pagination.total"
                            :items="dataGrid"
                            :loading="loading"
                            item-value="id"
                            @update:options="getGrid"
                        >
                            <template v-slot:thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <div style="display:flex; gap:4px;">
                                                <v-text-field v-model="fecha_desde" type="date" hide-details class="ma-2" density="compact"></v-text-field>
                                                <v-text-field v-model="fecha_hasta" type="date" hide-details class="ma-2" density="compact"></v-text-field>
                                            </div>
                                        </td>
                                        <td>
                                            <v-text-field v-model="idorder" hide-details placeholder="Search order id..." class="ma-2" density="compact"></v-text-field>
                                        </td>
                                        <td>
                                            <v-select
                                                v-model="nivel"
                                                :items="['', 'INFO', 'WARNING', 'ERROR', 'CRITICAL']"
                                                hide-details
                                                placeholder="Level..."
                                                class="ma-2"
                                                density="compact"
                                            ></v-select>
                                        </td>
                                        <td>
                                            <v-text-field v-model="mensaje" hide-details placeholder="Search message..." class="ma-2" density="compact"></v-text-field>
                                        </td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </template>
                            <template v-slot:item.nivel="{ item }">
                                <v-chip size="small" :color="nivelColor(item.nivel)" variant="flat">{{ item.nivel }}</v-chip>
                            </template>
                            <template v-slot:item.order_id="{ item }">
                                <span v-if="getOrderId(item)">
                                    <router-link :to="{ name: 'edit_order', params: { id: getOrderId(item) } }">#{{ getOrderId(item) }}</router-link>
                                </span>
                                <span v-else>-</span>
                            </template>
                            <template v-slot:item.mensaje="{ item }">
                                <span>{{ item.mensaje }}</span>
                            </template>
                            <template v-slot:item.actions="{ item }">
                                <v-btn
                                    size="small"
                                    variant="tonal"
                                    :disabled="!item.contexto"
                                    @click.stop="openLogDetail(item)"
                                >Ver contexto</v-btn>
                            </template>
                        </v-data-table-server>
                        <v-alert v-if="messageError != ''" :type="typeOfMessage" :text="messageError"></v-alert>
                    </div>
                </div>
            </div>
        </div>

        <v-dialog v-model="isLogDialogOpen" max-width="700">
            <v-card>
                <v-card-title>Contexto del log #{{ selectedLog?.id }}</v-card-title>
                <v-card-text>
                    <pre style="white-space: pre-wrap; word-break: break-word;">{{ formattedContext }}</pre>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="isLogDialogOpen = false">Cerrar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { getGridLogService } from '@/services/LogService'

const route = useRoute()

const dataGrid = ref([])

const pagination = reactive({
  from: 0,
  to: 0,
  total: 0,
  current: 1,
  lastPage: 0
})

const itemsPerPage = ref(10)

const headers = [
  { title: 'Fecha', align: 'center', sortable: true, key: 'fecha' },
  { title: 'Pedido', align: 'center', sortable: false, key: 'order_id' },
  { title: 'Nivel', align: 'center', sortable: true, key: 'nivel' },
  { title: 'Mensaje', align: 'start', sortable: false, key: 'mensaje' },
  { title: 'Acciones', align: 'center', sortable: false, key: 'actions' },
]

const loading = ref(true)

// Filtros
const idorder = ref('')
const nivel = ref('')
const mensaje = ref('')
const fecha_desde = ref('')
const fecha_hasta = ref('')

const search = ref('')

const messageError = ref('')
const typeOfMessage = ref('')

const isLogDialogOpen = ref(false)
const selectedLog = ref(null)

watch([idorder, nivel, mensaje, fecha_desde, fecha_hasta], () => {
  search.value = String(Date.now())
})

const formattedContext = computed(() => {
  if (!selectedLog.value?.contexto) return '-'
  try {
    return JSON.stringify(selectedLog.value.contexto, null, 2)
  } catch (e) {
    return String(selectedLog.value.contexto)
  }
})

function nivelColor(nivel) {
  switch ((nivel || '').toUpperCase()) {
    case 'ERROR':
    case 'CRITICAL':
      return 'error'
    case 'WARNING':
      return 'warning'
    default:
      return 'info'
  }
}

function getOrderId(item) {
  return item?.contexto?.order_id ?? null
}

function openLogDetail(item) {
  selectedLog.value = item
  isLogDialogOpen.value = true
}

async function getGrid({ page = 1, itemsPerPage = 10, sortBy = [] } = {}) {
  loading.value = true
  try {
    const data = await getGridLogService({
      page: page || pagination.current,
      itemsPerPage: itemsPerPage || itemsPerPage.value,
      sortBy: sortBy || [],
      idorder: idorder.value,
      nivel: nivel.value,
      mensaje: mensaje.value,
      fecha_desde: fecha_desde.value,
      fecha_hasta: fecha_hasta.value
    })

    dataGrid.value = data.data || []
    pagination.from = data.from
    pagination.to = data.to
    pagination.total = data.total
    pagination.current = data.current_page
    pagination.lastPage = data.last_page

  } catch (error) {
    messageError.value = 'Error! No se pudo obtener el listado de logs.'
    typeOfMessage.value = 'error'
    setTimeout(() => (messageError.value = ''), 6000)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  if (route.query.idorder) {
    idorder.value = String(route.query.idorder)
  }
  getGrid({ page: pagination.current, itemsPerPage: itemsPerPage.value })
})
</script>
